// https://github.com/tldraw/tldraw/blob/5db3c1553e14edd14aa5f7c0fc85fc5efc352335/packages/store/src/lib/StoreQueries.ts#L0-L1
// Fixed some imports
import { computed, EMPTY_ARRAY, isUninitialized, RESET_VALUE, withDiff } from "@supersig/state";
import { isEqual } from "es-toolkit";
// import {
//   executeQuery,
//   objectMatchesQuery,
//   QueryExpression,
// } from "./executeQuery";
import { objectMapValues } from "@supersig/utils";
import { objectMatchesQuery } from "./executeQuery";
import { IncrementalSetConstructor } from "./IncrementalSetConstructor";
import { diffSets, intersectSets } from "./setUtils";
/**
 * A class that provides a 'namespace' for the various kinds of indexes one may wish to derive from
 * the record store.
 */ export class StoreQueries {
    atoms;
    history;
    constructor(atoms, history){
        this.atoms = atoms;
        this.history = history;
        this.indexCache = new Map();
        this.typeIndexCache = new Map();
        this.historyCache = new Map();
    }
    /**
   * A cache of derivations (indexes).
   *
   * @internal
   */ indexCache;
    /**
   * A cache of type-based indexes to avoid full scans during large operations.
   * Maps typeName to Set of record IDs of that type.
   */ typeIndexCache;
    /**
   * A cache of derivations (filtered histories).
   *
   * @internal
   */ historyCache;
    /**
   * Create a derivation that contains the hisotry for a given type
   *
   * @param typeName - The name of the type to filter by.
   * @returns A derivation that returns the ids of all records of the given type.
   * @public
   */ filterHistory(typeName) {
        if (this.historyCache.has(typeName)) {
            return this.historyCache.get(typeName);
        }
        const filtered = computed("filterHistory:" + typeName, (lastValue, lastComputedEpoch)=>{
            if (isUninitialized(lastValue)) {
                return this.history.get();
            }
            const diff = this.history.getDiffSince(lastComputedEpoch);
            if (diff === RESET_VALUE) return this.history.get();
            const res = {
                added: {},
                removed: {},
                updated: {}
            };
            let numAdded = 0;
            let numRemoved = 0;
            let numUpdated = 0;
            for (const changes of diff){
                for (const added of objectMapValues(changes.added)){
                    if (added.typeName === typeName) {
                        if (res.removed[added.id]) {
                            const original = res.removed[added.id];
                            delete res.removed[added.id];
                            numRemoved--;
                            if (original !== added) {
                                res.updated[added.id] = [
                                    original,
                                    added
                                ];
                                numUpdated++;
                            }
                        } else {
                            res.added[added.id] = added;
                            numAdded++;
                        }
                    }
                }
                for (const [from, to] of objectMapValues(changes.updated)){
                    if (to.typeName === typeName) {
                        if (res.added[to.id]) {
                            res.added[to.id] = to;
                        } else if (res.updated[to.id]) {
                            res.updated[to.id] = [
                                res.updated[to.id][0],
                                to
                            ];
                        } else {
                            res.updated[to.id] = [
                                from,
                                to
                            ];
                            numUpdated++;
                        }
                    }
                }
                for (const removed of objectMapValues(changes.removed)){
                    if (removed.typeName === typeName) {
                        if (res.added[removed.id]) {
                            // was added during this diff sequence, so just undo the add
                            delete res.added[removed.id];
                            numAdded--;
                        } else if (res.updated[removed.id]) {
                            // remove oldest version
                            res.removed[removed.id] = res.updated[removed.id][0];
                            delete res.updated[removed.id];
                            numUpdated--;
                            numRemoved++;
                        } else {
                            res.removed[removed.id] = removed;
                            numRemoved++;
                        }
                    }
                }
            }
            if (numAdded || numRemoved || numUpdated) {
                return withDiff(this.history.get(), res);
            } else {
                return lastValue;
            }
        }, {
            historyLength: 100
        });
        this.historyCache.set(typeName, filtered);
        return filtered;
    }
    /**
   * Get or create a type-based index for efficient type filtering.
   * This avoids the need to scan all atoms when filtering by type.
   * Especially important for large stores as it avoids the need to scan all atoms and THEN filtering by type.
   */ filterIndex(typeName) {
        let typeIndex = this.typeIndexCache.get(typeName);
        if (!typeIndex) {
            const typeHistory = this.filterHistory(typeName);
            typeIndex = computed(`typeIndex:${typeName}`, (prevValue, lastComputedEpoch)=>{
                if (isUninitialized(prevValue)) {
                    // initial computation scanning all atoms once (this is what we used to always do)
                    typeHistory.get() // Subscribe to history
                    ;
                    const result = new Set();
                    for (const atom of objectMapValues(this.atoms.get())){
                        const record = atom.get();
                        if (record.typeName === typeName) {
                            result.add(record.id);
                        }
                    }
                    return result;
                }
                // incremental update using history diffs
                const history = typeHistory.getDiffSince(lastComputedEpoch);
                if (history === RESET_VALUE) {
                    // fall back to full atom scan if needed
                    const result = new Set();
                    for (const atom of objectMapValues(this.atoms.get())){
                        const record = atom.get();
                        if (record.typeName === typeName) {
                            result.add(record.id);
                        }
                    }
                    return result;
                }
                // apply incremental changes - history is an array of diffs
                const result = new Set(prevValue);
                for (const diff of history){
                    // add new records of this type
                    for (const record of objectMapValues(diff.added)){
                        if (record.typeName === typeName) {
                            result.add(record.id);
                        }
                    }
                    // handle updated records (type shouldn't change, but include for completeness)
                    for (const [from, to] of objectMapValues(diff.updated)){
                        if (to.typeName === typeName) {
                            result.add(to.id);
                        }
                    }
                    // remove deleted records
                    for (const record of objectMapValues(diff.removed)){
                        if (record.typeName === typeName) {
                            result.delete(record.id);
                        }
                    }
                }
                return result;
            }, {
                historyLength: 50
            });
            this.typeIndexCache.set(typeName, typeIndex);
        }
        return typeIndex;
    }
    /**
   * Create a derivation that returns an index on a property for the given type.
   *
   * @param typeName - The name of the type.
   * @param property - The name of the property.
   * @public
   */ index(typeName, property) {
        const cacheKey = typeName + ":" + property;
        if (this.indexCache.has(cacheKey)) {
            return this.indexCache.get(cacheKey);
        }
        const index = this.__uncached_createIndex(typeName, property);
        this.indexCache.set(cacheKey, index);
        return index;
    }
    /**
   * Create a derivation that returns an index on a property for the given type.
   *
   * @param typeName - The name of the type?.
   * @param property - The name of the property?.
   * @internal
   */ __uncached_createIndex(typeName, property) {
        const typeHistory = this.filterHistory(typeName);
        const fromScratch = ()=>{
            // deref typeHistory early so that the first time the incremental version runs
            // it gets a diff to work with instead of having to bail to this from-scratch version
            typeHistory.get();
            const res = new Map();
            for (const atom of objectMapValues(this.atoms.get())){
                const record = atom.get();
                if (record.typeName === typeName) {
                    const value = record[property];
                    if (!res.has(value)) {
                        res.set(value, new Set());
                    }
                    res.get(value).add(record.id);
                }
            }
            return res;
        };
        return computed("index:" + typeName + ":" + property, (prevValue, lastComputedEpoch)=>{
            if (isUninitialized(prevValue)) return fromScratch();
            const history = typeHistory.getDiffSince(lastComputedEpoch);
            if (history === RESET_VALUE) {
                return fromScratch();
            }
            const setConstructors = new Map();
            const add = (value, id)=>{
                let setConstructor = setConstructors.get(value);
                if (!setConstructor) setConstructor = new IncrementalSetConstructor(prevValue.get(value) ?? new Set());
                setConstructor.add(id);
                setConstructors.set(value, setConstructor);
            };
            const remove = (value, id)=>{
                let set = setConstructors.get(value);
                if (!set) set = new IncrementalSetConstructor(prevValue.get(value) ?? new Set());
                set.remove(id);
                setConstructors.set(value, set);
            };
            for (const changes of history){
                for (const record of objectMapValues(changes.added)){
                    if (record.typeName === typeName) {
                        const value = record[property];
                        add(value, record.id);
                    }
                }
                for (const [from, to] of objectMapValues(changes.updated)){
                    if (to.typeName === typeName) {
                        const prev = from[property];
                        const next = to[property];
                        if (prev !== next) {
                            remove(prev, to.id);
                            add(next, to.id);
                        }
                    }
                }
                for (const record of objectMapValues(changes.removed)){
                    if (record.typeName === typeName) {
                        const value = record[property];
                        remove(value, record.id);
                    }
                }
            }
            let nextValue = undefined;
            let nextDiff = undefined;
            for (const [value, setConstructor] of setConstructors){
                const result = setConstructor.get();
                if (!result) continue;
                if (!nextValue) nextValue = new Map(prevValue);
                if (!nextDiff) nextDiff = new Map();
                if (result.value.size === 0) {
                    nextValue.delete(value);
                } else {
                    nextValue.set(value, result.value);
                }
                nextDiff.set(value, result.diff);
            }
            if (nextValue && nextDiff) {
                return withDiff(nextValue, nextDiff);
            }
            return prevValue;
        }, {
            historyLength: 100
        });
    }
    /**
   * Create a derivation that will return a signle record matching the given query.
   *
   * It will return undefined if there is no matching record
   *
   * @param typeName - The name of the type?
   * @param queryCreator - A function that returns the query expression.
   * @param name - (optinal) The name of the query.
   */ record(typeName, queryCreator = ()=>({}), name = "record:" + typeName + (queryCreator ? ":" + queryCreator.toString() : "")) {
        const ids = this.ids(typeName, queryCreator, name);
        return computed(name, ()=>{
            for (const id of ids.get()){
                return this.atoms.get()[id]?.get();
            }
            return undefined;
        });
    }
    /**
   * Create a derivation that will return an array of records matching the given query
   *
   * @param typeName - The name of the type?
   * @param queryCreator - A function that returns the query expression.
   * @param name - (optinal) The name of the query.
   */ records(typeName, queryCreator = ()=>({}), name = "records:" + typeName + (queryCreator ? ":" + queryCreator.toString() : "")) {
        const ids = this.ids(typeName, queryCreator, "ids:" + name);
        return computed(name, ()=>{
            return [
                ...ids.get()
            ].map((id)=>{
                const atom = this.atoms.get()[id];
                if (!atom) {
                    throw new Error("no atom found for record id: " + id);
                }
                return atom.get();
            });
        });
    }
    /**
   * Create a derivation that will return the ids of all records of the given type.
   *
   * @param typeName - The name of the type.
   * @param queryCreator - A function that returns the query expression.
   * @param name - (optinal) The name of the query.
   */ ids(typeName, queryCreator = ()=>({}), name = "ids:" + typeName + (queryCreator ? ":" + queryCreator.toString() : "")) {
        const typeHistory = this.filterHistory(typeName);
        const fromScratch = ()=>{
            // deref type history early to allow first incremental update to use diffs
            typeHistory.get();
            const query = queryCreator();
            if (Object.keys(query).length === 0) {
                return this.filterIndex(typeName).get();
            }
            return executeQuery(this, typeName, query);
        };
        const fromScratchWithDiff = (prevValue)=>{
            const nextValue = fromScratch();
            const diff = diffSets(prevValue, nextValue);
            if (diff) {
                return withDiff(nextValue, diff);
            } else {
                return prevValue;
            }
        };
        const cachedQuery = computed("ids_query:" + name, queryCreator, {
            isEqual
        });
        return computed("query:" + name, (prevValue, lastComputedEpoch)=>{
            const query = cachedQuery.get();
            if (isUninitialized(prevValue)) {
                return fromScratch();
            }
            // if the query changed since last time this ran then we need to start again
            if (lastComputedEpoch < cachedQuery.lastChangedEpoch) {
                return fromScratchWithDiff(prevValue);
            }
            // otherwise iterate over the changes from the store and apply them to the previous value if needed
            const history = typeHistory.getDiffSince(lastComputedEpoch);
            if (history === RESET_VALUE) {
                return fromScratchWithDiff(prevValue);
            }
            const setConstructor = new IncrementalSetConstructor(prevValue);
            for (const changes of history){
                for (const added of objectMapValues(changes.added)){
                    if (added.typeName === typeName && objectMatchesQuery(query, added)) {
                        setConstructor.add(added.id);
                    }
                }
                for (const [_, updated] of objectMapValues(changes.updated)){
                    if (updated.typeName === typeName) {
                        if (objectMatchesQuery(query, updated)) {
                            setConstructor.add(updated.id);
                        } else {
                            setConstructor.remove(updated.id);
                        }
                    }
                }
                for (const removed of objectMapValues(changes.removed)){
                    if (removed.typeName === typeName) {
                        setConstructor.remove(removed.id);
                    }
                }
            }
            const result = setConstructor.get();
            if (!result) {
                return prevValue;
            }
            return withDiff(result.value, result.diff);
        }, {
            historyLength: 50
        });
    }
    exec(typeName, query) {
        const ids = executeQuery(this, typeName, query);
        if (ids.size === 0) {
            return EMPTY_ARRAY;
        }
        const atoms = this.atoms.get();
        return [
            ...ids
        ].map((id)=>atoms[id].get());
    }
}
export function executeQuery(store, typeName, query) {
    const matchIds = Object.fromEntries(Object.keys(query).map((key)=>[
            key,
            new Set()
        ]));
    for (const [k, matcher] of Object.entries(query)){
        if ("eq" in matcher) {
            const index = store.index(typeName, k);
            const ids = index.get().get(matcher.eq);
            if (ids) {
                for (const id of ids){
                    matchIds[k].add(id);
                }
            }
        } else if ("neq" in matcher) {
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                if (value !== matcher.neq) {
                    for (const id of ids){
                        matchIds[k].add(id);
                    }
                }
            }
        } else if ("gt" in matcher) {
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                if (value > matcher.gt) {
                    for (const id of ids){
                        matchIds[k].add(id);
                    }
                }
            }
        } else if ("stw" in matcher) {
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                if (typeof value === "string" && value.startsWith(matcher.stw)) {
                    for (const id of ids){
                        matchIds[k].add(id);
                    }
                }
            }
        } else if ("in" in matcher) {
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                if (matcher.in.includes(value)) {
                    for (const id of ids){
                        matchIds[k].add(id);
                    }
                }
            }
        } else if ("inStw" in matcher) {
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                if ((typeof value === "string" || typeof value === "number") && matcher.inStw.some((s)=>value.toString().startsWith(s))) {
                    for (const id of ids){
                        matchIds[k].add(id);
                    }
                }
            }
        } else if ("hasProperty" in matcher) {
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                for (const id of ids){
                    if (typeof value === "object" && value !== null && matcher.hasProperty in value) {
                        // make sure it isn't tombstoned
                        if (value[matcher.hasProperty] !== undefined && "type" in value[matcher.hasProperty] && value[matcher.hasProperty].type !== "tombstone") {
                            matchIds[k].add(id);
                        }
                    }
                }
            }
        }
    }
    return intersectSets(Object.values(matchIds));
}
